import { c as _c } from "react/compiler-runtime";
import { IconButton } from "@/components/ui/IconButton";
import { Spinner } from "@/components/ui/Spinner";
import { XIcon } from "lucide-react";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type * as React from "react";
import cn from "twsx";
import styles from "./List.module.css";

// Row selection chrome shared by FilterList and static lists: `ListSelectionHeader`
// is the count-and-buttons strip above the rows; `ListSelectionBar` floats the
// bulk actions over the bottom of the nearest positioned list surface while
// anything is selected, with its actions grouped by `ListSelectionBarGroup`.
export function ListSelectionHeader(t0) {
  const $ = _c(8);
  const {
    label,
    inset: t1,
    children
  } = t0;
  const inset = t1 === undefined ? "list" : t1;
  const t2 = inset === "flush" ? "px-4" : styles.contentInset;
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex flex-wrap items-center gap-2 border-b py-2 text-sm", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== label) {
    t4 = <span aria-live="polite" className="mr-auto text-text-secondary tabular-nums">{label}</span>;
    $[2] = label;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== t3 || $[6] !== t4) {
    t5 = <div className={t3}>{t4}{children}</div>;
    $[4] = children;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

/** Use when people select rows in a list and act on them together. */
export function ListSelectionBar(t0) {
  const $ = _c(14);
  const {
    open,
    count,
    label,
    onClear,
    children
  } = t0;
  const t1 = open ? "open" : "closed";
  const t2 = !open;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("pointer-events-auto flex h-11 w-fit max-w-full flex-row items-center gap-1.5 overflow-x-auto py-1.5 pl-4 pr-1.5", "rounded-full border border-border-primary bg-bg-surface text-text-primary shadow-pop", "data-[state=closed]:pointer-events-none data-[state=closed]:opacity-0");
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="text-text-secondary">selected</span>;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== count) {
    t5 = <span className="mr-1.5 shrink-0 whitespace-nowrap text-sm tabular-nums">{count} {t4}</span>;
    $[2] = count;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <XIcon />;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== onClear) {
    t7 = <IconButton label="Clear selection" variant="ghost" onClick={onClear}>{t6}</IconButton>;
    $[5] = onClear;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== children || $[8] !== label || $[9] !== t1 || $[10] !== t2 || $[11] !== t5 || $[12] !== t7) {
    t8 = <div className="pointer-events-none absolute inset-x-0 bottom-14 z-40 flex justify-center px-4"><div aria-label={label} data-state={t1} inert={t2} className={t3}>{t5}{children}{t7}</div></div>;
    $[7] = children;
    $[8] = label;
    $[9] = t1;
    $[10] = t2;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
export function ListSelectionBarGroup(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="flex shrink-0 items-center gap-1.5">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// Bar actions are compact pill buttons, Linear's bulk-action style. `segment`
// is kept as an alias so existing call sites render the same pill.
const pill = "h-8 gap-1.5 rounded-full border border-border-primary bg-bg-surface px-3 focus-visible:ring-2 focus-visible:ring-accent";
const listSelectionBarActionVariants = cva(["flex shrink-0 items-center whitespace-nowrap text-sm font-medium outline-none hover:bg-fill-hover", "disabled:pointer-events-none disabled:opacity-disabled-control [&>svg]:size-3.5 [&>svg]:shrink-0"], {
  variants: {
    layout: {
      action: pill,
      segment: pill
    },
    tone: {
      default: "text-text-primary",
      secondary: "text-text-secondary",
      danger: "text-red-text"
    }
  },
  defaultVariants: {
    layout: "action",
    tone: "default"
  }
});
export function ListSelectionBarAction(t0) {
  const $ = _c(19);
  let children;
  let destructive;
  let icon;
  let isLoading;
  let layout;
  let props;
  let tone;
  if ($[0] !== t0) {
    ({
      icon,
      isLoading,
      destructive,
      layout,
      tone,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = destructive;
    $[3] = icon;
    $[4] = isLoading;
    $[5] = layout;
    $[6] = props;
    $[7] = tone;
  } else {
    children = $[1];
    destructive = $[2];
    icon = $[3];
    isLoading = $[4];
    layout = $[5];
    props = $[6];
    tone = $[7];
  }
  const t1 = destructive ? "danger" : tone;
  let t2;
  if ($[8] !== layout || $[9] !== t1) {
    t2 = listSelectionBarActionVariants({
      layout,
      tone: t1
    });
    $[8] = layout;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== icon || $[12] !== isLoading) {
    t3 = isLoading ? <Spinner size="sm" /> : icon;
    $[11] = icon;
    $[12] = isLoading;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== children || $[15] !== props || $[16] !== t2 || $[17] !== t3) {
    t4 = <button type="button" className={t2} {...props}>{t3}{children}</button>;
    $[14] = children;
    $[15] = props;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
}